import type { Circuit, CircuitRoadPiece, TrackCell } from '@multirx/shared/types/track.js';
import { Direction, TrackCellType } from '@multirx/shared/types/track.js';
import { getCorePath, isPathClosed } from '@multirx/shared/game/circuitEdit.js';
import { areCellsAdjacent } from '@multirx/shared/game/circuitGeometryEdit.js';
import { coveredOffsets } from '@multirx/shared/game/circuitRoadPieceEdit.js';
import { getCircuitCellStyle } from '@multirx/shared/game/circuitSurfaceStyle.js';

export type Track3dTileKind = 'straight' | 'corner' | 'start' | 'startMerge';

export interface Track3dTile {
  x: number;
  y: number;
  kind: Track3dTileKind;
  /** Rotation de la pièce de route autour de l'axe Y, en pas de 90° (0 à 3). */
  rotationSteps: number;
  /**
   * Direction de déplacement en sortie de case (0 à 3), pour orienter une voiture.
   * Distincte de `rotationSteps` sur un virage : celui-ci indexe la rotation de la
   * pièce (quels côtés de la case elle relie), pas le sens de circulation.
   */
  facingSteps: number;
  /**
   * Bordure vibreur (rouge/blanc) à afficher sur cette case : toujours vrai sur un
   * virage, et aussi vrai sur une ligne droite directement adjacente à un virage —
   * pour éviter une rupture visuelle au milieu d'un enchaînement de virages
   * (chicane, « S ») — sinon la ligne droite garde son trottoir gris d'origine.
   */
  curbed: boolean;
  /**
   * Slug catalogue explicite (voir `Circuit.roadPieces`) si cette case est
   * l'ancre d'une pièce groupée (celle qui reçoit effectivement le modèle 3D
   * multi-case) ; sinon, le rendu utilise le modèle par défaut associé à `kind`.
   */
  pieceSlug?: string;
  /**
   * Case couverte par une pièce groupée mais qui n'est PAS son ancre (voir
   * `pieceSlug`) — le modèle de l'ancre s'étend visuellement par-dessus cette
   * case (même convention de pivot Kenney sur toute la famille Small/Large/
   * Larger, voir `circuit3dTrackScene.ts`), donc aucun rendu par défaut ici
   * (éviterait un doublon/chevauchement avec l'ancre).
   */
  pieceCovered?: boolean;
  /**
   * Case appartenant à la branche du tour joker (voir `buildJokerLap3dTiles`)
   * — permet au rendu (`circuit3dTrackScene.ts`) de distinguer visuellement
   * cette portion du tracé principal (bordure violette plutôt que blanche sur
   * les lignes droites), sans dupliquer la logique de calcul de tuile.
   */
  jokerLap?: boolean;
  /**
   * Case peinte en terre (voir `shared/game/circuitSurfaceStyle.ts#setCircuitCellFill`) —
   * cosmétique pour l'instant : seule la couleur de la chaussée change au rendu
   * (`circuit3dTrackScene.ts`), sans effet sur la physique/vitesse de la voiture.
   */
  earth?: boolean;
  /**
   * Cette case change de revêtement par rapport à la case précédente/suivante
   * sur le tracé (terre → bitume ou l'inverse) — permet au rendu de fondre la
   * chaussée d'une couleur vers l'autre à la jonction plutôt qu'une coupure nette
   * (voir `circuit3dTrackScene.ts#buildStraightSurfaceMaterial`). Sans effet si
   * les deux cases voisines ont le même revêtement.
   */
  earthFadeIn?: boolean;
  earthFadeOut?: boolean;
}

interface Delta {
  dx: number;
  dy: number;
}

function directionBetween(a: TrackCell, b: TrackCell): Delta {
  return { dx: b.x - a.x, dy: b.y - a.y };
}

/** Pas de 90° d'une direction axée sur la grille : est=0, sud=1, ouest=2, nord=3. */
function directionSteps({ dx, dy }: Delta): number {
  if (dx === 1) return 0;
  if (dy === 1) return 1;
  if (dx === -1) return 2;
  return 3; // dy === -1
}

/**
 * Convertit un pas `rotationSteps`/`facingSteps` (convention locale à ce module :
 * est=0, sud=1, ouest=2, nord=3, choisie pour aligner `rotation.y = -(steps*90°)`
 * en Three.js) vers l'enum partagé `Direction` (nord=0, est=1, sud=2, ouest=3,
 * utilisé par `CircuitRoadPiece.rotation`) — les deux encodages ne coïncident PAS,
 * décaler d'un cran est nécessaire avant toute comparaison entre les deux.
 */
export function tileStepToDirection(step: number): Direction {
  return (step + 1) % 4;
}

/** Calcule la tuile 3D d'une case à partir de ses voisines directes (entrée/sortie). */
function computeTile(cell: TrackCell, prev: TrackCell, next: TrackCell): Track3dTile {
  const stepIn = directionSteps(directionBetween(prev, cell));
  const stepOut = directionSteps(directionBetween(cell, next));

  if (cell.type === TrackCellType.START) {
    return {
      x: cell.x,
      y: cell.y,
      kind: 'start',
      rotationSteps: stepOut,
      facingSteps: stepOut,
      curbed: false,
    };
  }
  if (stepIn === stepOut) {
    return {
      x: cell.x,
      y: cell.y,
      kind: 'straight',
      rotationSteps: stepOut,
      facingSteps: stepOut,
      curbed: false,
    };
  }
  // Virage à angle droit : (stepOut - stepIn) mod 4 vaut 1 (à droite) ou 3 (à gauche).
  // La pièce doit toucher les côtés {(stepIn+2)%4, stepOut} de la case (entrée
  // opposée à stepIn, sortie vers stepOut) : à droite r=stepIn, à gauche r=stepIn+1.
  const turnsRight = (stepOut - stepIn + 4) % 4 === 1;
  const rotationSteps = turnsRight ? stepIn : (stepIn + 1) % 4;
  return {
    x: cell.x,
    y: cell.y,
    kind: 'corner',
    rotationSteps,
    facingSteps: stepOut,
    curbed: true,
  };
}

/**
 * Étend la bordure vibreur des virages aux lignes droites directement adjacentes
 * (case précédente ou suivante) — sinon un enchaînement virage/ligne droite/virage
 * rapproché (chicane, « S ») afficherait une rupture visuelle au milieu du virage.
 */
function markAdjacentStraightCurbs(tiles: Track3dTile[], loop: boolean): Track3dTile[] {
  const n = tiles.length;
  return tiles.map((tile, i) => {
    if (tile.kind !== 'straight') return tile;
    const prev = loop ? tiles[(i - 1 + n) % n] : tiles[i - 1];
    const next = loop ? tiles[(i + 1) % n] : tiles[i + 1];
    const curbed = prev?.kind === 'corner' || next?.kind === 'corner';
    return curbed === tile.curbed ? tile : { ...tile, curbed };
  });
}

/**
 * Reporte la peinture de surface par case (`Circuit.cellStyles`, voir
 * `circuitSurfaceStyle.ts`) sur les tuiles calculées — seule `fill: 'earth'` a un
 * rendu dédié pour l'instant (`asphalt`/`damier` restent visuellement identiques
 * au bitume par défaut, voir `circuit3dTrackScene.ts`).
 */
function applySurfaceFill(tiles: Track3dTile[], circuit: Circuit): Track3dTile[] {
  return tiles.map((tile) => {
    const earth = getCircuitCellStyle(circuit, tile.x, tile.y)?.fill === 'earth';
    return earth === !!tile.earth ? tile : { ...tile, earth };
  });
}

/**
 * Repère les jonctions terre/bitume le long du tracé (case dont le revêtement
 * diffère de la précédente et/ou de la suivante) — permet au rendu de fondre la
 * couleur plutôt que de couper net (voir `circuit3dTrackScene.ts`). Même
 * principe que `markAdjacentStraightCurbs` (comparaison aux voisins directs).
 */
function markSurfaceTransitions(tiles: Track3dTile[], loop: boolean): Track3dTile[] {
  const n = tiles.length;
  return tiles.map((tile, i) => {
    const prev = loop ? tiles[(i - 1 + n) % n] : tiles[i - 1];
    const next = loop ? tiles[(i + 1) % n] : tiles[i + 1];
    const earthFadeIn = !!prev && !!prev.earth !== !!tile.earth;
    const earthFadeOut = !!next && !!next.earth !== !!tile.earth;
    if (earthFadeIn === !!tile.earthFadeIn && earthFadeOut === !!tile.earthFadeOut) return tile;
    return { ...tile, earthFadeIn, earthFadeOut };
  });
}

/**
 * Applique les slugs catalogue des pièces groupées (`Circuit.roadPieces`) aux
 * tuiles correspondantes — pure couche de rendu, ne modifie ni les cases du
 * tracé ni la logique d'adjacence/rotation ci-dessus. Seule la case "ancre"
 * (décalage 0, voir `coveredOffsets`) reçoit `pieceSlug` — un seul modèle 3D
 * est instancié par pièce (voir `circuit3dTrackScene.ts`), pas une copie par
 * case couverte ; les autres cases sont marquées `pieceCovered` (pas de rendu
 * par défaut, le modèle de l'ancre les couvre déjà visuellement).
 */
function applyRoadPieceSlugs(
  tiles: Track3dTile[],
  pieces: CircuitRoadPiece[] | undefined,
  source: CircuitRoadPiece['source']
): Track3dTile[] {
  const relevant = (pieces ?? []).filter((p) => p.source === source);
  if (relevant.length === 0) return tiles;
  const n = tiles.length;
  const next = tiles.slice();
  for (const piece of relevant) {
    for (const o of coveredOffsets(piece)) {
      const idx = piece.startIndex + o;
      if (idx < 0 || idx >= n) continue;
      next[idx] =
        o === 0 ? { ...next[idx], pieceSlug: piece.modelId } : { ...next[idx], pieceCovered: true };
    }
  }
  return next;
}

/**
 * Variante pour le tracé principal : `startIndex` est relatif au path "core"
 * (sans la case de fermeture dupliquée, voir `getCorePath`) et boucle modulo
 * sa longueur. La case de fermeture elle-même (dupliquée en fin de `tiles`)
 * n'est jamais indexée ici (le bouclage modulo reste dans `[0, coreLen[`) —
 * elle est entièrement prise en charge par `buildTrack3dTiles`, qui la fait
 * toujours pointer sur la case départ (index 0), piece groupée ou non.
 */
function applyMainPathRoadPieceSlugs(
  tiles: Track3dTile[],
  circuit: Circuit,
  pieces: CircuitRoadPiece[] | undefined
): Track3dTile[] {
  const relevant = (pieces ?? []).filter((p) => p.source === 'path');
  if (relevant.length === 0) return tiles;
  const coreLen = getCorePath(circuit.path).length;
  if (coreLen <= 0) return tiles;
  const closed = isPathClosed(circuit.path);
  const next = tiles.slice();
  for (const piece of relevant) {
    for (const o of coveredOffsets(piece)) {
      const rawIdx = piece.startIndex + o;
      // Le bouclage modulo n'a de sens que pour une boucle RÉELLEMENT fermée
      // (franchissement légitime de la couture) — sur un tracé encore ouvert
      // ou raccourci (ex: undo sans purge de `roadPieces`, voir
      // `circuit3dEditor.ts`), un index hors plage ne doit pas retomber par
      // hasard sur une case sans rapport avec la pièce, mais être ignoré (bug
      // réel, repéré en testant l'undo après un virage groupé).
      const coreIdx = closed ? ((rawIdx % coreLen) + coreLen) % coreLen : rawIdx;
      if (coreIdx < 0 || coreIdx >= coreLen) continue;
      next[coreIdx] =
        o === 0
          ? { ...next[coreIdx], pieceSlug: piece.modelId }
          : { ...next[coreIdx], pieceCovered: true };
    }
  }
  return next;
}

/**
 * Calcule la disposition 3D (nature de la pièce + rotation) de chaque case
 * du tracé principal, à partir des cases voisines (le tracé est une boucle).
 * Ne couvre pas la ligne de départ ni le tour joker (extensions hors boucle).
 */
export function buildTrack3dTiles(circuit: Circuit): Track3dTile[] {
  const path = circuit.path;
  const n = path.length;
  if (n < 2) return [];

  const tiles = path.map((cell, i) => {
    const prev = path[(i - 1 + n) % n];
    const wrappedNext = path[(i + 1) % n];
    // Cette fonction boucle toujours modulo `n`, y compris pour une boucle
    // "conceptuellement" fermée sans case de fermeture dupliquée (voir les cas
    // de test) — ce bouclage n'est un problème QUE quand la case obtenue n'est
    // pas réellement adjacente : sur un tracé encore ouvert (mode Tracer, avant
    // fermeture), la dernière case boucle vers le départ, potentiellement très
    // loin, ce qui lui donnerait une direction sortante bidon et la classerait
    // à tort en virage (petit modèle par défaut, un vrai artefact visuel repéré
    // à la pointe du tracé en testant un virage large juste avant). On prolonge
    // alors la même direction que l'entrée pour classer la pointe en ligne droite.
    const next = areCellsAdjacent(cell.x, cell.y, wrappedNext.x, wrappedNext.y)
      ? wrappedNext
      : { x: cell.x + (cell.x - prev.x), y: cell.y + (cell.y - prev.y), type: cell.type };
    return computeTile(cell, prev, next);
  });

  // Case de fermeture dupliquée (même position que la case départ, voir
  // `getCorePath`) : son "suivant" recalculé ci-dessus (`path[0]`, à distance
  // nulle d'elle-même) ne correspond à aucune direction réelle, donc
  // `computeTile` la classe au hasard — sans ce repli elle s'affiche par-dessus
  // la case départ déjà rendue par l'index 0 (le double-affichage "ligne
  // droite sur une autre" au raccord du dernier virage). Corrigé AVANT
  // `markAdjacentStraightCurbs` : sinon la case précédente baserait son bord
  // vibreur sur cette classification erronée plutôt que sur la vraie case
  // départ (kind 'start', jamais 'corner').
  if (isPathClosed(path)) {
    tiles[n - 1] = { ...tiles[0], pieceCovered: true };
  }

  const curbedTiles = markAdjacentStraightCurbs(tiles, true);
  const filledTiles = applySurfaceFill(
    applyMainPathRoadPieceSlugs(curbedTiles, circuit, circuit.roadPieces),
    circuit
  );
  return markSurfaceTransitions(filledTiles, true);
}

/**
 * Calcule la disposition 3D de la ligne de départ (extension hors circuit, en ligne
 * droite garantie par l'éditeur — voir `circuitStartLaneEdit.ts`) : chaque case y
 * est traitée comme case de départ (kind 'start'), pas seulement la dernière (fond
 * de grille) — toute la ligne doit se distinguer visuellement du reste du tracé.
 * La case la plus proche du circuit (`sl.cells[0]`, adjacente à `joinCell` — seule
 * case dont l'adjacence au point de jonction est garantie à l'édition, voir
 * `circuitStartLaneEdit.ts#tryAppendStartLaneCell`) reçoit `kind: 'startMerge'` —
 * distinct de 'start' pour repérer la case de jonction avec le circuit, mais
 * rendue à l'identique (même bordure jaune) : une bretelle évasée y détonnait
 * avec le reste du tracé.
 */
export function buildStartLane3dTiles(circuit: Circuit): Track3dTile[] {
  const sl = circuit.startLane;
  if (!sl || sl.cells.length === 0 || sl.joinPathIndex < 0) return [];
  const joinCell = circuit.path[sl.joinPathIndex];
  if (!joinCell) return [];

  // Ligne droite garantie à l'édition : une seule direction pour toute la ligne,
  // de la case la plus loin (fond de grille, index 0 ici) vers la piste principale.
  const facingSteps = directionSteps(directionBetween(sl.cells[0], joinCell));

  const tiles: Track3dTile[] = sl.cells.map((cell, i) => ({
    x: cell.x,
    y: cell.y,
    kind: i === 0 ? 'startMerge' : 'start',
    rotationSteps: facingSteps,
    facingSteps,
    curbed: false,
  }));
  return applySurfaceFill(applyRoadPieceSlugs(tiles, circuit.roadPieces, 'startLane'), circuit);
}

/**
 * Calcule la disposition 3D des cases du tour joker (branche non bouclée).
 * L'entrée/la sortie de la branche se raccordent au tracé principal
 * (`entryPathIndex`/`exitPathIndex`), sans dupliquer ces cases dans le résultat.
 */
export function buildJokerLap3dTiles(circuit: Circuit): Track3dTile[] {
  const jl = circuit.jokerLap;
  if (!jl || jl.cells.length === 0 || jl.entryPathIndex < 0 || jl.exitPathIndex < 0) return [];
  const entryCell = circuit.path[jl.entryPathIndex];
  const exitCell = circuit.path[jl.exitPathIndex];
  if (!entryCell || !exitCell) return [];

  const cells = jl.cells;
  const n = cells.length;
  const tiles = cells.map((cell, i) => {
    const prev = i === 0 ? entryCell : cells[i - 1];
    const next = i === n - 1 ? exitCell : cells[i + 1];
    return computeTile(cell, prev, next);
  });
  const curbedTiles = markAdjacentStraightCurbs(tiles, false).map((tile) => ({
    ...tile,
    jokerLap: true,
  }));
  const filledTiles = applySurfaceFill(
    applyRoadPieceSlugs(curbedTiles, circuit.roadPieces, 'jokerLap'),
    circuit
  );
  return markSurfaceTransitions(filledTiles, false);
}
